import api from '../http.service';
import { API_ENDPOINTS } from '@/config/api.config';

const BASE = API_ENDPOINTS.STUDIO.INTERNAL_CHAT;

export interface InternalConversationMetadata {
  subject?: string | null;
  initiatedBy?: string | null;
  participantId?: string | null;
}

export interface InternalConversation {
  id: string;
  tenant_id: string;
  source: string;
  status: string;
  metadata: InternalConversationMetadata | null;
  user_id: string;
  initiator_full_name?: string | null;
  initiator_email?: string | null;
  participant_full_name?: string | null;
  participant_email?: string | null;
  initiator_avatar_url?: string | null;
  participant_avatar_url?: string | null;
  last_message_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface InternalMessageContent {
  text?: string;
  [key: string]: unknown;
}

export interface InternalMessage {
  id: string;
  conversation_id: string;
  sender_type: 'user' | 'agent' | string;
  sender_id: string | null;
  content: InternalMessageContent;
  sender_full_name?: string | null;
  sender_email?: string | null;
  created_at: string;
}

export interface WidgetSettings {
  chatWidgetEnabled: boolean;
  chatWidgetEnabledForUsers: boolean;
  platformFeatureEnabled?: boolean;
  tenantChatEnabled?: boolean;
}

export interface UpdateWidgetSettingsData {
  chatWidgetEnabled?: boolean;
  chatWidgetEnabledForUsers?: boolean;
}

export interface CreateInternalConversationData {
  subject?: string;
  participantId?: string;
}

export interface ListInternalConversationsParams {
  limit?: number;
  offset?: number;
  status?: string;
}

export interface InternalConversationsListResult {
  conversations: InternalConversation[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface ListInternalMessagesParams {
  limit?: number;
  offset?: number;
}

export interface InternalMessagesResult {
  messages: InternalMessage[];
}

export const getWidgetSettings = async (): Promise<WidgetSettings> => {
  const response = await api.get<{ success: boolean; data: WidgetSettings }>(
    `${BASE}/widget-settings`,
  );
  return response.data.data;
};

export const updateWidgetSettings = async (
  data: UpdateWidgetSettingsData,
): Promise<WidgetSettings> => {
  const response = await api.patch<{ success: boolean; data: WidgetSettings }>(
    `${BASE}/widget-settings`,
    data,
  );
  return response.data.data;
};

export const createConversation = async (
  data: CreateInternalConversationData,
): Promise<InternalConversation> => {
  const response = await api.post<{ success: boolean; data: InternalConversation }>(
    `${BASE}/conversations`,
    data,
  );
  return response.data.data;
};

export const listConversations = async (
  params: ListInternalConversationsParams = {},
): Promise<InternalConversationsListResult> => {
  const response = await api.get<{
    success: boolean;
    data: InternalConversationsListResult;
  }>(`${BASE}/conversations`, { params });
  return response.data.data;
};

export const getConversation = async (id: string): Promise<InternalConversation> => {
  const response = await api.get<{ success: boolean; data: InternalConversation }>(
    `${BASE}/conversations/${id}`,
  );
  return response.data.data;
};

export const sendMessage = async (
  conversationId: string,
  content: string,
): Promise<InternalMessage> => {
  const response = await api.post<{ success: boolean; data: InternalMessage }>(
    `${BASE}/conversations/${conversationId}/messages`,
    { content },
  );
  return response.data.data;
};

export const getMessages = async (
  conversationId: string,
  params: ListInternalMessagesParams = {},
): Promise<InternalMessagesResult> => {
  const response = await api.get<{ success: boolean; data: InternalMessagesResult }>(
    `${BASE}/conversations/${conversationId}/messages`,
    { params },
  );
  return response.data.data;
};
